// Ported from packages/workflow-engine-upstream/ui/src/Conversation.tsx at commit bebc382; only imports changed.
// Column 1: the question, then the run's events as they happen, with each ask as a card.
import { useState } from 'react';
import type { Ask, RunState } from './api';
import { eventLine, typedText } from './conversation-text';

type Answer = (resumeToken: string, values: Record<string, string>) => Promise<void>;
type SendFile = (file: File) => Promise<void>;

/**
 * Picking a method file sends it at once: one click, no second button. While the model reads it,
 * the button says so; an error shows next to it.
 */
function FilePick({ label, enabled, onFile }: { label: string; enabled: boolean; onFile: SendFile }) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const pick = async (file: File | undefined) => {
    if (!file) return;
    setSending(true);
    setError('');
    try { await onFile(file); } catch (e) { setError((e as Error).message); } finally { setSending(false); }
  };
  return (
    <span className="file-pick">
      <label className={enabled && !sending ? 'btn' : 'btn off'}>
        {sending ? 'Reading your file…' : label}
        <input type="file" accept=".pdf,.md,.markdown,.txt" hidden disabled={!enabled || sending}
          onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ''; }} />
      </label>
      {error && <span className="note err">{error}</span>}
    </span>
  );
}

function AskCard({ ask, open, onAnswer, onFile }: { ask: Ask; open: boolean; onAnswer: Answer; onFile: SendFile }) {
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(ask.slots.filter((s) => s.safeDefault !== null).map((s) => [s.slot, s.safeDefault as string])));
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const interview = ask.slots.some((s) => s.kind === 'method' && s.options.length === 0);
  const enabled = open && !sending;
  const submit = async (answers: Record<string, string>) => {
    setSending(true);
    try { await onAnswer(ask.resumeToken, answers); } finally { setSending(false); }
  };
  const title = ask.stepId ? `Mid-run ask · step ${ask.stepId}` : `Ask · ${ask.slots.length} ${ask.slots.length === 1 ? 'question' : 'questions'}`;
  const complete = interview ? !!message.trim() || Object.values(values).some((v) => v.trim()) : ask.slots.every((s) => values[s.slot]?.trim());
  return (
    <div className={open ? 'ask' : 'ask closed'}>
      <b>{title}</b>
      {ask.slots.map((s) => (
        <div key={s.slot} style={{ display: 'grid', gap: 4 }}>
          <span>
            <b className="mono">{s.slot}</b> <span className="note">{s.reason}{s.attempted.length ? ` Tried: ${s.attempted.join(', ')}.` : ''}</span>
          </span>
          <div className="chips">
            {s.options.map((o) => s.slot === 'method' && o.value === 'send_file' ? (
              // The "send a file" door opens the file picker itself; the file goes as soon as it is picked.
              <FilePick key={o.value} label={o.label} enabled={enabled} onFile={onFile} />
            ) : (
              <button key={o.value} type="button" disabled={!enabled} className={values[s.slot] === o.value ? 'sel' : ''} onClick={() => setValues({ ...values, [s.slot]: o.value })}>
                {o.label}
                {/* The value the plan will use, when the label doesn't show it. */}
                {o.label.toLowerCase().includes(o.value.toLowerCase()) ? '' : <span className="mono note"> = {o.value}</span>}
                {o.value === s.safeDefault ? ' (default)' : ''}
              </button>
            ))}
          </div>
          {(s.options.length === 0 || s.kind === 'binding') && (s.kind === 'method' ? (
            <textarea aria-label={s.slot} rows={3} disabled={!enabled} value={values[s.slot] ?? ''}
              onChange={(e) => setValues({ ...values, [s.slot]: e.target.value })} placeholder="Write your answer…" />
          ) : (
            // A chosen option, the default among them, shows on its chip; the box holds only what was typed.
            <input aria-label={s.slot} disabled={!enabled} value={typedText(s, values[s.slot])}
              onChange={(e) => setValues({ ...values, [s.slot]: e.target.value })} placeholder="Write your answer…" />
          ))}
        </div>
      ))}
      {interview && (
        <label className="method-message">
          Answer several questions together
          <textarea rows={6} disabled={!enabled} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your answers in your own words. The engine builds the ordered method." />
          <span className="note">Every question is optional. Answer what applies; the engine plans from what you give.</span>
        </label>
      )}
      {open ? (
        <div className="btns">
          <button type="button" className="p" disabled={!complete || sending} onClick={() => void submit(message.trim() ? { ...values, message } : values)}>
            {sending ? 'Sending…' : 'Answer'}
          </button>
          <button type="button" disabled={sending || ask.slots.some((s) => s.safeDefault === null)} onClick={() => void submit({})}>
            Use defaults
          </button>
        </div>
      ) : (
        <span className="note">Closed.</span>
      )}
    </div>
  );
}

export function Conversation({ s, onAnswer, onFile }: { s: RunState; onAnswer: Answer; onFile: SendFile }) {
  return (
    <div className="col">
      <span className="h">1 · Conversation</span>
      <div className="msg u">{s.question}</div>
      {s.events.map((e) => {
        if (e.type === 'ask_opened') {
          const ask = s.trace.asks.find((a) => a.askId === e.payload?.askId);
          return ask ? <AskCard key={`${s.runId}:${e.seq}`} ask={ask} open={ask.askId === s.openAskId} onAnswer={onAnswer} onFile={onFile} /> : null;
        }
        const text = eventLine(e, s);
        return text ? (
          <div key={e.seq} className="msg">
            {text}
          </div>
        ) : null;
      })}
      {s.refusal && (
        <div className="msg">
          <b>No procedure fits.</b>
          <div>Searched:</div>
          <ul>{s.refusal.searched?.map((item, i) => <li key={i}>{item}</li>)}</ul>
          <div>Available documents:</div>
          <ul>{s.refusal.existing?.map((item, i) => <li key={i}>{item}</li>)}</ul>
          <span className="note">Send a method file or select Talk it through in the ask.</span>
        </div>
      )}
      {/* With no procedure found, the ask holds the file button; otherwise a file can still be sent before approval. */}
      {!s.refusal && <FileCard key={s.runId} enabled={!s.events.some((e) => e.type === 'plan_approved') && ['parked', 'awaiting_approval', 'awaiting_ask'].includes(s.status)} onFile={onFile} />}
    </div>
  );
}

function FileCard({ enabled, onFile }: { enabled: boolean; onFile: SendFile }) {
  return (
    <div className="drop method-file">
      <FilePick label="Send a method file" enabled={enabled} onFile={onFile} />
      <span className="note">PDF, Markdown, or plain text. Maximum 7 MB. Send the file before approval.</span>
    </div>
  );
}
